/**
 * Kai schedule-snapshot artifact types — mirror
 * `tideway-kai/src/orchestrator/artifacts/schedule_snapshot.rs`.
 */

import type { ApiBooking, ScheduleResponse } from "@/types/api";
import type { ScheduleMilestone } from "@/types/schedule";

export type KaiScheduleMode =
  | "sailing_lookup"
  | "booking_matched"
  | "booking_milestones";

export interface KaiScheduleContext {
  origin_input?: string | null;
  destination_input?: string | null;
  origin_locodes?: string | null;
  dest_locodes?: string | null;
  carrier?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  source_id?: string | null;
}

export interface KaiScheduleRange {
  from: string;
  to: string;
}

export interface KaiScheduleSnapshot {
  mode: KaiScheduleMode;
  title?: string | null;
  context: KaiScheduleContext;
  /** Full `ScheduleResponse` rows for sailing_lookup / booking_matched. */
  schedules: ScheduleResponse[];
  /** Booking rows for booking_milestones (API shape). */
  bookings: ApiBooking[];
  view?: string | null;
  kinds: ScheduleMilestone[];
  range?: KaiScheduleRange | null;
  pagination?: unknown;
  footnotes?: string[];
  open_in_schedule_url: string;
}

export interface KaiScheduleSnapshotRefSpec {
  id: string;
  artifact?: unknown;
}

const VALID_MODES = new Set<KaiScheduleMode>([
  "sailing_lookup",
  "booking_matched",
  "booking_milestones",
]);

const VALID_KINDS = new Set<ScheduleMilestone>([
  "departure",
  "arrival",
  "cutoff",
  "stuffing",
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function optString(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

function parseContext(raw: unknown): KaiScheduleContext {
  if (!isRecord(raw)) return {};
  return {
    origin_input: optString(raw.origin_input),
    destination_input: optString(raw.destination_input),
    origin_locodes: optString(raw.origin_locodes),
    dest_locodes: optString(raw.dest_locodes),
    carrier: optString(raw.carrier),
    date_from: optString(raw.date_from),
    date_to: optString(raw.date_to),
    source_id: optString(raw.source_id),
  };
}

/** Parse + validate a `schedule_snapshot` fenced body or artifact payload. */
export function parseScheduleSnapshotSpec(
  body: string,
): KaiScheduleSnapshot | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;

    const mode = typeof parsed.mode === "string" ? parsed.mode.trim() : "";
    if (!VALID_MODES.has(mode as KaiScheduleMode)) return null;

    const openUrl =
      typeof parsed.open_in_schedule_url === "string"
        ? parsed.open_in_schedule_url.trim()
        : "";
    if (!openUrl) return null;

    const schedules = Array.isArray(parsed.schedules)
      ? (parsed.schedules.filter(isRecord) as unknown as ScheduleResponse[])
      : [];
    const bookings = Array.isArray(parsed.bookings)
      ? (parsed.bookings.filter(isRecord) as unknown as ApiBooking[])
      : [];

    const kinds = Array.isArray(parsed.kinds)
      ? (parsed.kinds.filter(
          (k): k is ScheduleMilestone =>
            typeof k === "string" && VALID_KINDS.has(k as ScheduleMilestone),
        ) as ScheduleMilestone[])
      : [];

    const footnotes = Array.isArray(parsed.footnotes)
      ? parsed.footnotes.filter(
          (f): f is string => typeof f === "string" && f.trim() !== "",
        )
      : [];

    let range: KaiScheduleRange | null = null;
    if (isRecord(parsed.range)) {
      const from = optString(parsed.range.from);
      const to = optString(parsed.range.to);
      if (from && to) range = { from, to };
    }

    return {
      mode: mode as KaiScheduleMode,
      title: typeof parsed.title === "string" ? parsed.title : null,
      context: parseContext(parsed.context),
      schedules,
      bookings,
      view: optString(parsed.view),
      kinds,
      range,
      pagination: parsed.pagination,
      footnotes,
      open_in_schedule_url: openUrl,
    };
  } catch {
    return null;
  }
}

/** Parse a compact `schedule_snapshot_ref` fence body `{ "id": "..." }`. */
export function parseScheduleSnapshotRefSpec(
  body: string,
): KaiScheduleSnapshotRefSpec | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (!isRecord(parsed)) return null;
    const id = typeof parsed.id === "string" ? parsed.id.trim() : "";
    if (!id) return null;
    return { id, artifact: parsed.artifact };
  } catch {
    return null;
  }
}
